import { Component, AfterViewInit, ViewChild, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';

// Importaciones de servicios.
import { LogService } from '../../../services/log.service';

// Importaciones de componentes.
import { LogDeleteAllDialogComponent } from '../../../components/log-delete-all-dialog/log-delete-all-dialog.component';

// Importaciones de interfaces.
import { ILog } from '../../../interfaces/log.interface';
import { ILogResponse } from '../../../interfaces/log-response.interface';

// Importaciones de modelos.
import { Employee } from '../../../models/employee.model';
import { Log } from '../../../models/log.model';

/**
 * Componente de configuración de los registros.
 *
 * Este componente proporciona una interfaz para gestionar la configuración de los registros dentro de la aplicación.
 * Permite realizar ajustes relacionados con los registros.
 */
@Component({
  selector: 'app-logs-settings',
  templateUrl: './logs-settings.component.html',
  standalone: false,
})
export class LogsSettingsComponent implements OnInit, AfterViewInit {
  /**
   * Indica si los filtros deben ser mostrados en la interfaz de usuario.
   * Es un valor booleano que controla la visibilidad de la sección de filtros.
   */
  showFilters: boolean = false;

  /**
   * Indica si los registros están siendo cargados.
   *
   * Esta propiedad se utiliza para mostrar un indicador de carga mientras
   * se recuperan los registros de la fuente de datos.
   */
  loading: boolean = false;

  /**
   * Lista de registros que contiene los objetos de tipo `ILog`.
   * Se utiliza para almacenar y manipular los registros en la aplicación.
   */
  logs: ILog[] = [];

  /**
   * Objeto de tipo `FormGroup` que agrupa los controles del formulario de filtros.
   * Se utiliza para gestionar y validar los campos de filtro en la interfaz.
   */
  form: FormGroup = new FormGroup({
    employeeName: new FormControl(),
    ipAddress: new FormControl(),
    startDate: new FormControl(),
    endDate: new FormControl(),
  });

  /**
   * Columnas que se mostrarán en la tabla de los registros.
   *
   * Esta propiedad define las columnas que se mostrarán en la tabla de los registros, incluyendo los campos
   * relacionados con la información básica del registro.
   */
  tableColumns: string[] = [
    'createdAt',
    'employee.displayName',
    'ipAddress',
    'description',
    'userAgent',
  ];

  /**
   * Datos de la tabla de los registros.
   *
   * Esta propiedad contiene los datos de los registros que se mostrarán en la tabla, utilizando `MatTableDataSource`
   * para manejar y mostrar los datos de manera eficiente, incluyendo funcionalidades como paginación y filtrado.
   */
  tableData: MatTableDataSource<ILog> = new MatTableDataSource<ILog>();

  /**
   * Paginador de la tabla de los registros.
   *
   * Esta propiedad se utiliza para la paginación de los datos en la tabla de los registros. Se hace referencia a un
   * componente `MatPaginator` que permite navegar entre las diferentes páginas de la tabla.
   */
  @ViewChild(MatPaginator) tablePaginator: MatPaginator | null = null;

  /**
   * Referencia al componente MatSort que se utiliza para habilitar la funcionalidad de ordenación en la tabla.
   *
   * Se utiliza el decorador `@ViewChild` para acceder a la instancia del componente MatSort asociado a la tabla de los registros.
   * Esto permite aplicar la funcionalidad de ordenación en las columnas de la tabla, permitiendo a los usuarios ordenar los
   * datos de forma ascendente o descendente según la columna seleccionada.
   *
   * @see MatSort - Componente de Angular Material que proporciona capacidades de ordenación para las tablas.
   */
  @ViewChild(MatSort) tableSort: MatSort | null = null;

  /**
   * Obtiene el control del formulario para el campo 'employeeName'.
   * Devuelve el control de tipo `AbstractControl` o `null` si no existe.
   */
  get formEmployeeName(): AbstractControl | null {
    return this.form.get('employeeName');
  }

  /**
   * Obtiene el control del formulario para el campo 'ipAddress'.
   * Devuelve el control de tipo `AbstractControl` o `null` si no existe.
   */
  get formIpAddress(): AbstractControl | null {
    return this.form.get('ipAddress');
  }

  /**
   * Obtiene el control del formulario para el campo 'startDate'.
   * Devuelve el control de tipo `AbstractControl` o `null` si no existe.
   */
  get formStartDate(): AbstractControl | null {
    return this.form.get('startDate');
  }

  /**
   * Obtiene el control del formulario para el campo 'endDate'.
   * Devuelve el control de tipo `AbstractControl` o `null` si no existe.
   */
  get formEndDate(): AbstractControl | null {
    return this.form.get('endDate');
  }

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param logService - Servicio para gestionar la información de los registros.
   * @param matDialog - Servicio para abrir cuadros de diálogo.
   */
  constructor(private logService: LogService, private matDialog: MatDialog) {}

  /**
   * Método del ciclo de vida de Angular que se ejecuta una vez que el componente
   * ha sido inicializado, pero antes de que se renderice la vista en el DOM.
   *
   * Este método se utiliza para inicializar datos o realizar cualquier operación
   * que deba llevarse a cabo antes de que el componente sea visible para el usuario.
   * En este caso, se llama a la función `initTable()` para inicializar y cargar
   * los datos necesarios para la tabla.
   */
  ngOnInit(): void {
    this.initTable();
  }

  /**
   * Método del ciclo de vida de Angular que se ejecuta después de que
   * la vista del componente y sus vistas hijas hayan sido completamente inicializadas.
   *
   * Este método se utiliza para realizar inicializaciones adicionales que
   * requieren acceso a las vistas del componente (como elementos DOM,
   * directivas, o componentes hijos).
   */
  ngAfterViewInit(): void {
    this.configureTable();
  }

  /**
   * Inicializa la tabla de los registros.
   *
   * Este método se encarga de configurar y cargar los datos necesarios para la tabla de los registros.
   * Puede incluir la obtención de datos desde un servicio, la configuración de la paginación
   * y otras configuraciones relacionadas con la visualización de la tabla.
   */
  private async initTable(): Promise<void> {
    this.loading = true;
    this.tableData = new MatTableDataSource<ILog>();

    try {
      // Obtener los registros y añadir-los a la tabla.
      const logResponse: ILogResponse = await this.logService.getAll();

      this.logs = logResponse.logs.map((log: ILog) => {
        return new Log({ ...log, employee: new Employee(log.employee) });
      });

      this.tableData = new MatTableDataSource<ILog>(this.logs);
    } finally {
      this.configureTable();
      this.loading = false;
    }
  }

  /**
   * Configura la tabla de datos con las opciones necesarias, como la ordenación y la paginación.
   *
   * Se asegura de que la tabla esté configurada correctamente para su visualización y que las
   * funcionalidades de paginación y ordenación estén habilitadas, si es necesario.
   */
  private configureTable(): void {
    this.tableData.data?.reverse();

    // Asignar el `paginator` y el `sort` si existen.
    this.tablePaginator && (this.tableData.paginator = this.tablePaginator);
    this.tableSort && (this.tableData.sort = this.tableSort);
  }

  /**
   * Recarga la tabla de los registros.
   *
   * Esta función se encarga de actualizar la vista de la tabla de los registros
   * para reflejar los cambios recientes en los datos.
   */
  reload(): void {
    this.initTable();
  }

  /**
   * Elimina todos los registros de la tabla.
   *
   * Este método se encarga de eliminar todos los registros de la tabla de registros.
   * La implementación de la eliminación puede variar según los requisitos de la aplicación.
   */
  deleteAll(): void {
    this.matDialog
      .open(LogDeleteAllDialogComponent, {
        width: '500px',
        maxWidth: '100%',
      })
      .afterClosed()
      .subscribe((isDeletedAll: boolean) => {
        if (isDeletedAll) {
          this.initTable();
        }
      });
  }

  /**
   * Filtra los registros según los criterios proporcionados en los formularios.
   *
   * Los filtros incluyen ID, nombre del empleado, dirección IP, fechas de inicio y fin.
   * La búsqueda es insensible a mayúsculas/minúsculas y las fechas se comparan con la fecha de creación.
   * Los filtros no se aplican si el campo correspondiente está vacío.
   */
  filter(): void {
    const employeeName = this.formEmployeeName?.value?.toLowerCase().trim();
    const ipAddress = this.formIpAddress?.value?.toLowerCase().trim();
    const startDate = this.formStartDate?.value
      ? new Date(this.formStartDate.value)
      : undefined;
    const endDate = this.formEndDate?.value
      ? new Date(this.formEndDate.value)
      : undefined;

    this.tableData.data = this.logs.filter((log: ILog) => {
      const logCreatedAt = log.createdAt ? new Date(log.createdAt) : undefined;

      return (
        (!employeeName ||
          log.employee.displayName?.toLowerCase().includes(employeeName)) &&
        (!ipAddress || log.ipAddress?.toLowerCase().includes(ipAddress)) &&
        (!startDate || (logCreatedAt && logCreatedAt >= startDate)) &&
        (!endDate || (logCreatedAt && logCreatedAt <= endDate))
      );
    });
  }
}
